import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Grid,
  Text,
  VStack,
  HStack,
  Link,
  Spinner,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import { FiArrowUpRight } from 'react-icons/fi';
import { getShopifyOrderInfo } from '../../../../services/flaskService';
import { ShopifyOrderInfo } from '../../../../types/shopify';
import { SHOPIFY_DATA_UNAVAILABLE_MESSAGE, getFulfillmentStatusProps } from '../../../../utils/shopifyUtils';
import { Collab, Campaign } from '@foodfluence/shared';
import ShopifyProductDisplay from './ShopifyProductDisplay';

interface ShopifyShippingTrackerProps {
  clientId: string;
  collab: Collab;
  campaign?: Campaign;
  refreshInterval?: number;
  /** Which order to track. Defaults to the collab's original order. */
  orderId?: string;
  /** Shown as a badge beside the order name. Only passed when a collab has more than one shipment. */
  label?: string;
}

const ShopifyShippingTracker: React.FC<ShopifyShippingTrackerProps> = ({
  clientId,
  collab,
  campaign,
  refreshInterval = 300000, // 5 minutes
  orderId,
  label
}) => {
  const trackedOrderId = orderId ?? collab?.shopifyOrderID;
  const [shippingInfo, setShippingInfo] = useState<ShopifyOrderInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchShippingUpdate = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const info = await getShopifyOrderInfo(clientId, trackedOrderId);
      if (!info.success) {
        setError(SHOPIFY_DATA_UNAVAILABLE_MESSAGE);
        setShippingInfo(null);
      } else {
        setShippingInfo(info);
      }
    } catch (error) {
      console.error('Error fetching shipping update:', error);
      setError(SHOPIFY_DATA_UNAVAILABLE_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchShippingUpdate();
    const interval = setInterval(fetchShippingUpdate, refreshInterval);
    return () => clearInterval(interval);
  }, [clientId, trackedOrderId, refreshInterval]);

  if (isLoading) {
    return (
      <Box p="16px" textAlign="center" fontFamily="display">
        <Spinner size="lg" color="#0E0E0E" thickness="3px" />
        <Text mt="8px" fontSize="14px" color="#55555A">Loading shipping information...</Text>
      </Box>
    );
  }

  if (error || !shippingInfo?.order) {
    return (
      <Box p="14px" borderRadius="16px" bg="#F2F2F0" fontFamily="display">
        <Text color="#3E3E42" fontSize="14px" lineHeight="1.4">
          {error || SHOPIFY_DATA_UNAVAILABLE_MESSAGE}
        </Text>
      </Box>
    );
  }

  const { order } = shippingInfo;
  const trackingRows = order.fulfillments.flatMap(f => f.trackingInfo);
  const hasTrackingInfo = trackingRows.length > 0;
  const status = order.displayFulfillmentStatus.toUpperCase();
  // Shopify reports fulfilment, not delivery, so the tracker stops at Shipped. ponytail: add
  // Out for delivery / Delivered steps once the order info carries carrier events.
  const isShipped = status === 'FULFILLED' || status === 'PARTIALLY_FULFILLED' || (status !== 'UNFULFILLED' && hasTrackingInfo);
  const steps = [
    { label: 'Ordered', reached: true },
    { label: 'Shipped', reached: isShipped },
  ];
  const currentIndex = isShipped ? 1 : 0;
  const fallback = collab?.shopifyShippingAddressFallback;
  const addressLine = [
    order.shippingAddress.address1 || fallback?.address1,
    order.shippingAddress.address2 || fallback?.address2,
    [order.shippingAddress.city || fallback?.city, order.shippingAddress.zip || fallback?.zip].filter(Boolean).join(' '),
    order.shippingAddress.country || fallback?.country,
  ].filter(Boolean).join(', ');
  const microLabel = {
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: '#66666A',
  };

  return (
    <Box
      as="section"
      aria-label={`Order ${order.name}`}
      fontFamily="display"
      color="#0E0E0E"
      border="1px solid #E4E4E0"
      borderRadius="20px"
      p="16px"
    >
      <VStack spacing="14px" align="stretch">
        <Flex align="flex-start" justify="space-between" gap="10px">
          <HStack spacing="8px" minW={0}>
            <Text fontSize="17px" fontWeight="600">Order {order.name}</Text>
            {label && (
              <Flex as="span" h="24px" px="8px" borderRadius="999px" bg="#0E0E0E" color="#C6F432" align="center" fontSize="11px" fontWeight="600">
                {label}
              </Flex>
            )}
          </HStack>
          <Flex
            as="span"
            flex="none"
            h="28px"
            px="10px"
            borderRadius="999px"
            align="center"
            fontSize="12px"
            fontWeight="600"
            whiteSpace="nowrap"
            bg={isShipped ? '#0E0E0E' : '#F0F0EE'}
            color={isShipped ? '#C6F432' : '#3E3E42'}
          >
            {getFulfillmentStatusProps(status).label}
          </Flex>
        </Flex>

        {/* Tracker */}
        <VStack spacing="8px" align="stretch">
          <Grid aria-hidden="true" templateColumns={`repeat(${steps.length}, minmax(0, 1fr))`}>
            {steps.map((step, i) => (
              <Flex key={step.label} align="center">
                <Box
                  flex="none"
                  w="14px"
                  h="14px"
                  borderRadius="999px"
                  boxSizing="border-box"
                  bg={step.reached ? (i === currentIndex ? '#C6F432' : '#0E0E0E') : '#FFFFFF'}
                  border={step.reached ? (i === currentIndex ? '3px solid #0E0E0E' : 'none') : '2px solid #B4B4B8'}
                />
                <Box
                  flex={1}
                  h="3px"
                  mx="2px"
                  borderRadius="999px"
                  bg={i === steps.length - 1 ? 'transparent' : i < currentIndex ? '#0E0E0E' : '#E4E4E0'}
                />
              </Flex>
            ))}
          </Grid>
          <Grid as="ol" aria-label="Tracking steps" listStyleType="none" m={0} p={0} gap="4px" templateColumns={`repeat(${steps.length}, minmax(0, 1fr))`}>
            {steps.map(step => (
              <Text
                as="li"
                key={step.label}
                fontSize="13px"
                lineHeight="1.2"
                fontWeight={step.reached ? '800' : '600'}
                color={step.reached ? '#0E0E0E' : '#66666A'}
              >
                {step.label}
              </Text>
            ))}
          </Grid>
        </VStack>

        {/* Products */}
        {campaign?.shopifyProducts && Object.keys(campaign?.shopifyProducts).length > 0 && (
          <Box pt="12px" borderTop="1px solid #ECECEA">
            <Text {...microLabel} mb="8px">Products</Text>
            <ShopifyProductDisplay
              clientId={clientId}
              productVariants={campaign.shopifyProducts}
              orderId={trackedOrderId}
            />
          </Box>
        )}

        {/* Tracking numbers */}
        {trackingRows.map((tracking, i) => (
          <Flex key={`${tracking.number}-${i}`} align="center" gap="10px" p="4px 4px 4px 12px" borderRadius="14px" bg="#F2F2F0">
            <Box flex={1} minW={0}>
              <Text {...microLabel}>Tracking #</Text>
              <Text fontSize="14px" fontWeight="600" letterSpacing="0.02em" noOfLines={1}>{tracking.number}</Text>
            </Box>
            {tracking.url && (
              <Link
                href={tracking.url}
                isExternal
                flex="none"
                h="44px"
                px="12px"
                borderRadius="12px"
                bg="#FFFFFF"
                display="flex"
                alignItems="center"
                gap="4px"
                fontSize="13px"
                fontWeight="600"
                color="#0E0E0E"
                _hover={{ textDecoration: 'none', bg: '#ECECEA' }}
                aria-label={`Track shipment ${tracking.number}`}
              >
                Track
                <Icon as={FiArrowUpRight} boxSize="14px" />
              </Link>
            )}
          </Flex>
        ))}

        {addressLine && (
          <Text fontSize="13px" lineHeight="1.4" color="#66666A">
            Ships to {addressLine}
          </Text>
        )}

        <Tooltip label="Shipping information updates every 5 minutes">
          <Text fontSize="12px" color="#66666A" w="fit-content">
            Last updated: {new Date().toLocaleTimeString()}
          </Text>
        </Tooltip>
      </VStack>
    </Box>
  );
};

export default ShopifyShippingTracker; 